import { useEffect, useState, type FormEvent } from "react";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import {
  ArrowRight,
  BarChart3,
  TrendingUp,
  Calendar,
  Mail,
  CheckCircle2,
} from "lucide-react";
import "./newsletter.css";

type SubmitState = "idle" | "submitting" | "success" | "error";

const HIGHLIGHTS = [
  {
    icon: BarChart3,
    title: "Race Analytics",
    description:
      "Deep dives into performance trends, split analysis, and statistical breakdowns across major endurance events.",
  },
  {
    icon: TrendingUp,
    title: "Athlete Spotlights",
    description:
      "Profiles of standout performances, rising stars, and historical comparisons backed by data.",
  },
  {
    icon: Calendar,
    title: "Season Previews",
    description:
      "Data-driven previews and recaps of major races, from IRONMAN World Championship to Boston Marathon.",
  },
];

export default function NewsletterPage() {
  const [revealed, setRevealed] = useState(false);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  const submitDisabled = submitState === "submitting" || !email.trim();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitDisabled) return;

    setSubmitState("submitting");
    setSubmitError(null);

    track("tr_newsletter_submit_attempt", {
      has_email: Boolean(email.trim()),
      has_first_name: Boolean(firstName.trim()),
      has_last_name: Boolean(lastName.trim()),
      source: "newsletter_page",
    });

    try {
      const response = await fetch(`${API_BASE}/newsletter/subscribe`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string }
        | null;

      if (!response.ok || !payload?.success) {
        throw new Error(
          payload?.message || "Could not subscribe. Please try again.",
        );
      }

      setSubmitState("success");
      setEmail("");
      setFirstName("");
      setLastName("");

      track("tr_newsletter_submit_success", { source: "newsletter_page" });
    } catch (error) {
      setSubmitState("error");
      setSubmitError(
        error instanceof Error ? error.message : "Could not subscribe.",
      );

      track("tr_newsletter_submit_error", {
        reason: error instanceof Error ? error.message : "unknown",
        source: "newsletter_page",
      });
    }
  }

  return (
    <div className="nl-page">
      {/* ── Hero ── */}
      <section className={cn("nl-hero", revealed && "revealed")}>
        <div className="nl-hero-inner">
          <span className="nl-kicker">// Endurance Data Newsletter</span>
          <h1 className="nl-title">
            Triathlon Data
            <br />
            <strong className="nl-title-accent">Insights</strong>
          </h1>
          <p className="nl-subtitle">
            Curious statistics, race breakdowns, and athlete spotlights
            delivered to your inbox. Built on the same data powering
            Tomorrow&apos;s Run.
          </p>
        </div>
      </section>

      {/* ── Signup (center of page) ── */}
      <section className={cn("nl-signup", revealed && "revealed")}>
        <div className="nl-signup-card">
          {submitState === "success" ? (
            <div className="nl-success" aria-live="polite">
              <CheckCircle2 className="nl-success-icon" />
              <p className="nl-success-heading">You&apos;re subscribed!</p>
              <p className="nl-success-text">
                Check your inbox for a welcome email. We&apos;ll keep you in
                the loop with endurance data insights.
              </p>
              <button
                type="button"
                className="nl-reset-btn"
                onClick={() => setSubmitState("idle")}
              >
                Subscribe another email
              </button>
            </div>
          ) : (
            <>
              <div className="nl-signup-header">
                <Mail className="nl-signup-icon" />
                <h2 className="nl-signup-heading">Subscribe</h2>
              </div>

              <form className="nl-form" onSubmit={handleSubmit}>
                <div className="nl-field">
                  <label className="nl-label" htmlFor="nl-email">
                    Email address
                  </label>
                  <input
                    id="nl-email"
                    type="email"
                    className="nl-input"
                    placeholder="you@example.com"
                    autoComplete="email"
                    maxLength={320}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="nl-name-row">
                  <div className="nl-field">
                    <label className="nl-label" htmlFor="nl-first-name">
                      First name{" "}
                      <span className="nl-optional">(optional)</span>
                    </label>
                    <input
                      id="nl-first-name"
                      type="text"
                      className="nl-input"
                      autoComplete="given-name"
                      maxLength={255}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="nl-field">
                    <label className="nl-label" htmlFor="nl-last-name">
                      Last name{" "}
                      <span className="nl-optional">(optional)</span>
                    </label>
                    <input
                      id="nl-last-name"
                      type="text"
                      className="nl-input"
                      autoComplete="family-name"
                      maxLength={255}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {submitError && (
                  <div className="nl-error">{submitError}</div>
                )}

                <button
                  type="submit"
                  className="nl-submit"
                  disabled={submitDisabled}
                >
                  {submitState === "submitting"
                    ? "Subscribing..."
                    : "Subscribe"}
                  {submitState !== "submitting" && (
                    <ArrowRight className="size-4" />
                  )}
                </button>

                <p className="nl-disclaimer">
                  No spam, ever. Unsubscribe at any time.
                </p>
              </form>
            </>
          )}
        </div>
      </section>

      {/* ── What you'll get ── */}
      <section className={cn("nl-highlights", revealed && "revealed")}>
        <div className="nl-highlights-inner">
          <span className="nl-kicker">// What you&apos;ll get</span>
          <h2 className="nl-highlights-heading">
            Data-driven endurance content
          </h2>

          <div className="nl-highlights-grid">
            {HIGHLIGHTS.map((item) => (
              <div className="nl-highlight" key={item.title}>
                <item.icon className="nl-highlight-icon" />
                <h3 className="nl-highlight-title">{item.title}</h3>
                <p className="nl-highlight-desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
